import {
	AutoComplete,
	AutoCompleteProps,
	Button,
	Card,
	Checkbox,
	Col,
	Collapse,
	CollapseProps,
	Divider,
	Flex,
	Form,
	FormProps,
	Input,
	Modal,
	Row,
	Table,
	TableProps,
} from 'antd';
import { Fragment, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';

// components
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NoData from 'components/noData/noData';
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';
import Icon from 'utils/icon';
import DisabledElement from 'components/disabledElement/disabledElement';
import FilePreview from 'components/filePreview/filePreview';
import FormElement from 'components/formElement/formElement';
import FileInput from 'components/fileInput/fileInput';
import { Attendee, AttendeeListItem } from '../models/attendance';
import { onAddAttendee, onGetAssemblyAttendeeById, onGetAssemblyAttendees, onSearchShareholder } from '../services/assembliesServices';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { isRTL } from 'core/services/language';
import { formatFileSize } from 'utils/FileSize';
import { dateFormat } from 'utils/dateFormat';
import { ActionShareholder } from '../../commonModels';
import { useDebounce } from 'use-debounce';
import { StatusColors } from 'utils/statusColors';

// services

// styles

// models

type FieldTypes = {
	delegate_name: string;
	delegate_file: any;
};

interface ActionAssembliesAttendanceListPageProps {}

const ActionAssembliesAttendanceListPage: FunctionComponent<ActionAssembliesAttendanceListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [attendeeForm] = Form.useForm();

	const [searchedQuery, setSearchedQuery] = useState<string>('');
	const [debouncedQuery] = useDebounce(searchedQuery, 500);

	const [attendeesQuery, setAttendeesQuery] = useState<string>('');
	const [debouncedAttendeesQuery] = useDebounce(attendeesQuery, 500);
	const lastAttendeesQuery = useRef<string>(debouncedAttendeesQuery);

	const { id } = useParams();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isAttendeeLoading, setIsAttendeeLoading] = useState<boolean>(false);
	const [canAdd, setCanAdd] = useState<boolean>(false);

	const [attendants, setAttendants] = useState<AttendeeListItem[]>([]);
	const [selectedAttendant, setselectedAttendant] = useState<Attendee>();
	const [vottingItems, setVottingItems] = useState<CollapseProps['items']>([]);

	const [searchedShareholders, setSearchedShareholders] = useState<ActionShareholder[]>([]);
	const [searchedShareholdersOptions, setSearchedShareholdersOptions] = useState<AutoCompleteProps['options']>([]);
	const [selectedSearchedShareholder, setSelectedSearchedShareholder] = useState<ActionShareholder>();
	const [isSearching, setIsSearching] = useState<boolean>(false);

	const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
	const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
	const [isDelegated, setIsDelegated] = useState<boolean>(false);
	const [isAddingAttendee, setIsAddingAttendee] = useState<boolean>(false);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<AttendeeListItem>['columns'] = [
		{
			title: t('COMMON.NAME'),
			dataIndex: 'attendee',
			key: 'name',
			render: (attendee) => (isRTL() ? attendee.name : (attendee.name_en ?? attendee.name)),
		},
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'attendee',
			key: 'national_id',
			render: (attendee) => attendee.national_id,
		},
		{
			title: t('ACTIONS.ATTENDEE_TYPE'),
			dataIndex: 'attendee_type',
			key: 'attendee_type',
			render: (attend_type, row) => (
				<Flex gap={5} justify="center" align="center">
					<StatusComponent
						text={attend_type === 'Member' ? t('ACTIONS.BOARD_MEMBER') : t('COMMON.SHAREHOLDER')}
						color={attend_type === 'Member' ? 'green' : 'blue'}
					/>
					{attend_type !== 'Member' && row.is_board_member && <StatusComponent text={t('ACTIONS.BOARD_MEMBER')} color="green" />}
				</Flex>
			),
		},
		{
			title: t('ACTIONS.ATTEND_TYPE'),
			dataIndex: 'attend_by',
			key: 'attend_by',
			render: (attend_by, row) => (
				<StatusComponent
					text={attend_by === 'CompanyUser' ? t('ACTIONS.COMPANY') : t('COMMON.TANAQUL_APP')}
					color={attend_by === 'CompanyUser' ? 'blue' : 'green'}
				/>
			),
		},
		{
			title: t('ACTIONS.ATTEND_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at) => dateFormat(created_at),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={10} justify="center">
					<ListButton type="view" onClick={() => viewShareholderHandler(record.id)} />
					{record.can_add_vote && <ListButton type="vote" onClick={() => navigate(`vote/${record.id}`)} />}
				</Flex>
			),
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getAttendees();
	}, []);

	useEffect(() => {
		setSearchedShareholdersOptions([]);
		setSearchedShareholders([]);

		if (debouncedQuery.length >= 3) {
			searchShareholderHandler(debouncedQuery);
		}
	}, [debouncedQuery]);

	useEffect(() => {
		if (lastAttendeesQuery.current === debouncedAttendeesQuery) return;
		lastAttendeesQuery.current = debouncedAttendeesQuery;

		const params = new URLSearchParams(location.search);
		params.set('page', '1');
		navigate(`${location.pathname}?${params.toString()}`, { replace: true });

		setCurrentPage(1);
		getAttendees(1);
	}, [debouncedAttendeesQuery]);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getAttendees = async (page: number = getCurrentPage()) => {
		try {
			setIsLoading(true);

			const response = await onGetAssemblyAttendees(id as string, page, debouncedAttendeesQuery.trim() || undefined);

			setAttendants(response.assembly_attendances);
			setTotalRecords(response.pagination.total);
			setCanAdd(response.can_add_attendance);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/assemblies');
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetAssemblyAttendees(id as string, page, debouncedAttendeesQuery.trim() || undefined);
			setAttendants(response.assembly_attendances);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const viewShareholderHandler = async (attendeeId: number) => {
		try {
			setIsDetailsModalOpen(true);

			setIsAttendeeLoading(true);

			const response = await onGetAssemblyAttendeeById(id as string, attendeeId);

			setselectedAttendant(response);

			if (response?.responses)
				setVottingItems([
					{
						id: '1',
						label: <p style={{ marginBottom: 0, fontSize: 15 }}>{t('ACTIONS.VOTTING_DETAILS')}</p>,
						children: response?.responses?.map((vote, index) => {
							return (
								<Fragment key={index}>
									<Flex align="center" gap={20}>
										<p className="mb-0">
											{index + 1} - {isRTL() ? vote.translated_title.ar : (vote.translated_title.en ?? vote.translated_title.ar)}
										</p>
										<StatusComponent small text={t(`ACTIONS.${vote.response?.toUpperCase()}`)} color={StatusColors[vote?.response] || 'orange'} />
									</Flex>

									{response.responses.length !== index + 1 && <Divider className="my-1" />}
								</Fragment>
							);
						}),
					},
				]);

			setIsAttendeeLoading(false);
		} catch (error) {
			setIsAttendeeLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsDetailsModalOpen(false);
		}
	};

	const searchShareholderHandler = async (text: string) => {
		try {
			setIsSearching(true);

			const response = await onSearchShareholder(id as string, text);

			setSearchedShareholders(response);

			setSearchedShareholdersOptions(
				response.map((shareholder) => ({
					label: (
						<Flex align="center" gap={10}>
							<Icon color="var(--primary)" name="profile" />
							<p className="mb-0 py-1" style={{ fontSize: 15 }}>{`${isRTL() ? shareholder.name : (shareholder.name_en ?? shareholder.name)}`}</p>
							<StatusComponent
								small
								color={shareholder.type === 'Member' ? 'green' : 'blue'}
								text={shareholder.type === 'Member' ? t('ACTIONS.BOARD_MEMBER') : t('COMMON.SHAREHOLDER')}
								style={{ fontSize: 11, marginInlineStart: 5 }}
							/>
						</Flex>
					),
					value: `${shareholder.id}`,
				})),
			);

			setIsSearching(false);
		} catch (error) {
			setIsSearching(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const selectShareholderHandler = (shareholderId: string) => {
		const selectedShareholder = searchedShareholders?.find((shareholder) => shareholder.id == shareholderId);
		setSelectedSearchedShareholder(selectedShareholder as ActionShareholder);
		setSearchedQuery('');
	};

	const closeAttendeeModalHandler = () => {
		setIsAddModalOpen(false);
		setSelectedSearchedShareholder(undefined);
		setSearchedQuery('');
		setSearchedShareholders([]);
		setSearchedShareholdersOptions([]);
		setIsDelegated(false);
	};

	const addAttendeeHandler: FormProps<FieldTypes>['onFinish'] = async (values) => {
		try {
			setIsAddingAttendee(true);

			const payload = new FormData();

			payload.append('national_id', selectedSearchedShareholder?.national_id as string);
			payload.append('is_delegate_attendance', isDelegated ? '1' : '0');

			if (isDelegated) {
				payload.append('delegate_name', values.delegate_name);
				payload.append('delegate_file', values.delegate_file);
			}

			await onAddAttendee(id as string, payload);

			closeAttendeeModalHandler();
			getAttendees();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ATTENDEE_ADDED_SUCCESSFULLY'));
			setIsAddingAttendee(false);
		} catch (error: any) {
			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
			setIsAddingAttendee(false);
		}
	};

	return (
		<>
			<PageTitle name={t('PAGES.ASSEMBLY_ATTENDANCE')} />

			<BackButton customBack={`/dashboard/actions/assemblies/view/${id}`} />

			<Card>
				<Flex justify="space-between" align="center" className="mb-4">
					<h5 style={{ marginBottom: 0 }}>{t('PAGES.ASSEMBLY_ATTENDANCE')}</h5>

					<Flex align="center" gap={20}>
						<Input
							allowClear
							style={{ width: '100%', minWidth: '300px', height: 'auto', paddingBlock: 4 }}
							placeholder={t('ACTIONS.SEARCH_IN_ATTENDANCE')}
							prefix={<Icon name="search" style={{ marginInlineEnd: 10, marginTop: -3 }} />}
							value={attendeesQuery}
							onChange={(e) => setAttendeesQuery(e.target.value)}
						/>

						{canAdd && (
							<Button
								type="primary"
								size="middle"
								onClick={() => {
									setIsAddModalOpen(true);
								}}>
								{t('ACTIONS.ADD_ASSEMBLY_ATTENDANCE')}
							</Button>
						)}
					</Flex>
				</Flex>

				<Table
					columns={columns}
					dataSource={attendants}
					scroll={{ x: 600 }}
					rowKey="id"
					loading={isLoading}
					pagination={{
						pageSize: 15,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			</Card>

			{/* details modal  */}
			<Modal
				open={isDetailsModalOpen}
				title={
					<Flex gap={10} align="center">
						<Icon name="info" width={30} height={30} />
						<h6 className="mb-0">{t('ACTIONS.ASSEMBLY_ATTENDANT_DETAILS')}</h6>
					</Flex>
				}
				footer={[]}
				onCancel={() => {
					setIsDetailsModalOpen(false);
					setselectedAttendant(undefined);
				}}>
				{isAttendeeLoading ? (
					<LoadingComponent />
				) : (
					<div className="mt-4">
						<DisabledElement fullWidth label={t('COMMON.NATIONAL_ID')} value={selectedAttendant?.attendance?.attendee?.national_id} />

						<DisabledElement
							fullWidth
							label={t('COMMON.SHAREHOLDER_FULL_NAME')}
							value={
								isRTL()
									? selectedAttendant?.attendance?.attendee?.name
									: (selectedAttendant?.attendance?.attendee?.name_en ?? selectedAttendant?.attendance?.attendee?.name)
							}
						/>

						<DisabledElement
							fullWidth
							label={t('ACTIONS.ATTENDEE_TYPE')}
							value={t(`ACTIONS.${selectedAttendant?.attendance.attendee.type?.toUpperCase()}`)}
						/>

						<DisabledElement
							fullWidth
							label={t('ACTIONS.ATTEND_TYPE')}
							value={selectedAttendant?.attendance?.attend_by === 'CompanyUser' ? t('ACTIONS.COMPANY') : t('COMMON.APPLICATION')}
						/>

						{selectedAttendant?.attendance?.by_company_user?.name && (
							<DisabledElement
								fullWidth
								label={t('ACTIONS.EMPLOYEE_WHO_HAS_REGISTERED')}
								value={selectedAttendant?.attendance?.by_company_user?.name}
							/>
						)}

						{!!selectedAttendant?.attendance?.is_delegate_attendance && (
							<>
								<DisabledElement fullWidth label={t('ACTIONS.DELEGATE_NAME')} value={selectedAttendant?.attendance?.delegate_name} />

								<div className="mt-4">
									<FilePreview
										name={t('ACTIONS.DELEGATION_DOCUMENT')}
										data={formatFileSize(selectedAttendant?.attendance?.delegate_document_path?.size_in_kilobyte || 0)}
										downloadLink={selectedAttendant?.attendance?.delegate_document_path?.download_link}
									/>
								</div>
							</>
						)}

						{selectedAttendant?.responses && selectedAttendant?.responses.length > 0 && (
							<Collapse
								className="mt-4"
								items={vottingItems}
								defaultActiveKey={['1']}
								expandIconPosition="end"
								expandIcon={(props) => (
									<Icon
										style={{ transform: props.isActive ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }}
										name="rightArrow"
										height={16}
										width={16}
										color="var(--primary)"
									/>
								)}
							/>
						)}

						{selectedAttendant?.can_add_vote && (
							<Button block type="primary" className="mt-3" onClick={() => navigate(`vote/${selectedAttendant?.attendance.id}`)}>
								{t('ACTIONS.ADD_VOTE')}
							</Button>
						)}
					</div>
				)}
			</Modal>

			{/* Search modal  */}
			<Modal
				open={isAddModalOpen}
				width={{
					sm: '90%',
					md: '80%',
					lg: '80%',
					xl: '50%',
					xxl: '40%',
				}}
				title={
					<Flex gap={10} align="center">
						<Icon name="info" width={30} height={30} />
						<h6 className="mb-0">{t('ACTIONS.ADD_ASSEMBLY_ATTENDANCE')}</h6>
					</Flex>
				}
				footer={[
					<Button key="discard" type="link" danger onClick={closeAttendeeModalHandler} size="middle">
						{t('COMMON.CANCEL')}
					</Button>,
					<Button key="confirm" type="primary" onClick={() => attendeeForm.submit()} size="middle" loading={isAddingAttendee}>
						{t('COMMON.CONFIRM')}
					</Button>,
				]}
				onCancel={closeAttendeeModalHandler}
				onClose={closeAttendeeModalHandler}
				okText={t('COMMON.CONFIRM')}>
				<div className="mt-4">
					<AutoComplete
						backfill
						style={{ width: '100%', height: 'auto' }}
						placeholder={t('ACTIONS.SEARCH_BY_NATIONAL_ID_OR_NAME')}
						prefix={<Icon name="search" style={{ marginInlineEnd: 10, marginTop: -3 }} />}
						options={searchedShareholdersOptions}
						onSelect={selectShareholderHandler}
						onSearch={setSearchedQuery}
						value={searchedQuery}
						notFoundContent={
							isSearching ? (
								<span>
									<LoadingOutlined style={{ marginInlineEnd: 10, color: 'var(--primary)' }} /> {t('COMMON.SEARCHING')}
								</span>
							) : (
								t('COMMON.NO_DATA_FOUND')
							)
						}
					/>

					{selectedSearchedShareholder && (
						<div className="containerDiv mt-3" style={{ backgroundColor: '#fafafa' }}>
							<Form form={attendeeForm} onFinish={addAttendeeHandler}>
								<Row gutter={[16, 16]}>
									<Col span={24} md={12}>
										<DisabledElement fullWidth label={t('COMMON.NATIONAL_ID')} value={selectedSearchedShareholder.national_id} />
									</Col>

									<Col span={24} md={12}>
										<DisabledElement
											fullWidth
											label={t('ACTIONS.SHAREHOLDER_FULL_NAME')}
											value={isRTL() ? selectedSearchedShareholder.name : (selectedSearchedShareholder.name_en ?? selectedSearchedShareholder.name)}
										/>
									</Col>

									<Col span={24}>
										<Checkbox onChange={(value) => setIsDelegated(value.target.checked)}>{t('ACTIONS.IS_DELEGATE_ATTENDANCE')}</Checkbox>
									</Col>

									{isDelegated && (
										<>
											<Col span={24} md={12}>
												<FormElement
													name="delegate_name"
													label={t('ACTIONS.DELEGATE_NAME')}
													fullWidth
													type="text"
													rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
												/>
											</Col>

											<Col span={24} md={12}>
												<Form.Item name="delegate_file" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
													<FileInput
														accept="pdf"
														name={t('ACTIONS.DELEGATE_FILE')}
														onSelect={(file) => {
															attendeeForm.setFieldValue('delegate_file', file);
														}}
													/>
												</Form.Item>
											</Col>
										</>
									)}
								</Row>
							</Form>
						</div>
					)}
				</div>
			</Modal>
		</>
	);
};

export default ActionAssembliesAttendanceListPage;
